import dayjs from 'dayjs';
import type { DaySequence } from '../generated/gql-types';

const epochMsOrZero = (value?: string | null): number => {
  if (value == null || value === '') {
    return 0;
  }
  const n = Number(value);
  return Number.isNaN(n) ? 0 : n;
};

/**
 * Single instant used to order rows **newest first** across web, client, and worker.
 * Matches row calendar semantics: segment {@link DaySequence.dateTime} first, then anchor
 * {@link DaySequence.startDate}, then {@link DaySequence.startDateISO}.
 */
const getDaySequenceSortInstant = (s: DaySequence): number => {
  const fromDateTime = epochMsOrZero(s.dateTime ?? null);
  if (fromDateTime) {
    return fromDateTime;
  }
  const fromStartDate = epochMsOrZero(s.startDate ?? null);
  if (fromStartDate) {
    return fromStartDate;
  }
  if (s.startDateISO) {
    const d = dayjs(s.startDateISO);
    return d.isValid() ? d.valueOf() : 0;
  }
  return 0;
};

const clockInTiebreak = (s: DaySequence): number =>
  epochMsOrZero(s.clockInTime ?? null);

/**
 * Comparator for **newest segment / anchor first**, then later clock-in, then id.
 * Suitable for `Array.prototype.sort`.
 */
export const compareDaySequencesNewestFirst = (
  a: DaySequence,
  b: DaySequence,
): number => {
  const primary = getDaySequenceSortInstant(b) - getDaySequenceSortInstant(a);
  if (primary !== 0) {
    return primary;
  }
  const secondary = clockInTiebreak(b) - clockInTiebreak(a);
  if (secondary !== 0) {
    return secondary;
  }
  return b.id.localeCompare(a.id);
};
